import type { Ring, Vec2 } from '../../../../geo/types';
import { horizontalCap, octahedronSolid, planPoint, pyramidOverRing, ringPoints } from '../../solids';
import { pointedArchRise, pointedWindow } from '../../panels';
import { centredWindow, rectWall } from '../masonry';
import { pinnacleSolid, squareRing } from '../gothic/pinnacle';
import { clockDial } from '../gothic/clockDial';
import { stringCourse } from '../gothic/bands';
import { needleSpire } from '../gothic/needleSpire';
import { ogeeDormer } from '../gothic/ogeeDormer';
import type { OudeKerkBuild } from './oudeKerkParts';
import { OUDE_KERK } from './oudeKerkPlan';
import { withoutTracery } from '../tracery/glazing';
import { LANDMARK_PATTERN, patternedWalls } from '../surfacePatterns';

/**
 * The tower of c.1325 (Janse 2004): a plinth and three stages of about 8.9 m in its own brick with red
 * sandstone quoins (surfacePatterns.ts), divided by stone string courses; paired lancets in the middle stage and the belfry openings in the top one, as the
 * woodcut draws them (uva 1775-1835 x 2360-2385). At the foot of the needle a dial to each quarter in
 * a lead-clad timber dormer flanked by pinnacles under an ogee with a pinnacle (Janse, p. 135), and
 * corner pinnacles as tall as the dormers (woodcut 1544). Opening, dial and dormer sizes are read from
 * the woodcut's proportions, not recorded.
 */
const TOWER = {
  stages: [0.5, 9.7, 18.9],
  course: { height: 0.3, outset: 0.12 },
  lancet: { width: 0.9, sill: 12, top: 17.2 },
  belfry: { width: 1.3, sill: 19.6, top: 24.1 },
  pinnacle: { inset: 0.45, half: 0.4, shaft: 2.6, tip: 5.3 },
  /** The needle's octagon stands this far inside the tower walls, clear of the corner pinnacles. */
  needleInset: 0.9,
  dormer: {
    width: 3.6,
    springRise: 2.8,
    apexRise: 4.4,
    /** The dormer's back stands this far from the tower's axis, inside the needle up to the dormer's apex. */
    backFromAxis: 1.9,
    dial: { rise: 1.5, radius: 1.0 },
    flank: { half: 0.22, shaft: 3.4, tip: 4.6 },
    crown: { half: 0.2, below: 0.5, shaft: 0.8, tip: 1.5 },
  },
  turretSlit: { width: 0.35, height: 1.1, every: 4 },
} as const;

function openingsOf(side: number): Ring[] {
  const pair = (w: number, sill: number, top: number): Ring[] =>
    [side / 3, (2 * side) / 3].map((c) => pointedWindow(c, sill, w, top - pointedArchRise(w), 3));
  return [...pair(TOWER.lancet.width, TOWER.lancet.sill, TOWER.lancet.top), ...pair(TOWER.belfry.width, TOWER.belfry.sill, TOWER.belfry.top)];
}

function buildShaft(s: OudeKerkBuild): void {
  const { center, half, bodyY } = s.plan.tower;
  const ring = squareRing(center, half);
  const openings = openingsOf(2 * half);
  const brick = patternedWalls(s.stone, LANDMARK_PATTERN.oudeKerkTower);
  const { width, top } = OUDE_KERK.portals.tower;
  const door = centredWindow(2 * half, 0, top, width / (2 * half));
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % 4]!;
    const west = from[0] < center[0] && to[0] < center[0];
    rectWall(s.builder, brick, s.frame, [from, to], [0, bodyY], west ? [...openings, ...door] : openings);
  });
  horizontalCap(s.builder, s.stone.wall, s.frame, ring, bodyY, true);
  const box = { u0: center[0] - half, u1: center[0] + half, v0: center[1] - half, v1: center[1] + half };
  for (const y of TOWER.stages.slice(1)) stringCourse(s.builder, s.stone.wall, s.frame, box, { y, ...TOWER.course });
}

/** One clock dormer on the tower face from `a` to `b` (counter-clockwise), with its dial and pinnacles. */
function buildClockDormer(s: OudeKerkBuild, a: Vec2, b: Vec2): void {
  const { half, bodyY } = s.plan.tower;
  const d = TOWER.dormer;
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const along: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
  const outward: Vec2 = [along[1], -along[0]];
  const mid: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const at = (k: number): Vec2 => [mid[0] + along[0] * k, mid[1] + along[1] * k];
  const [springY, apexY] = [bodyY + d.springRise, bodyY + d.apexRise];
  ogeeDormer(s.builder, s.lead, s.frame, { from: at(-d.width / 2), to: at(d.width / 2), y0: bodyY, springY, apexY, depth: half - d.backFromAxis });
  clockDial(s.builder, { face: s.stone.opening, rim: s.stone.wall }, s.frame, { at: mid, y: bodyY + d.dial.rise, radius: d.dial.radius, outward });
  for (const k of [-d.width / 2, d.width / 2]) {
    pinnacleSolid(s.builder, s.lead, s.frame, { center: at(k), half: d.flank.half, y0: bodyY, shaftTop: bodyY + d.flank.shaft, tipY: bodyY + d.flank.tip });
  }
  pinnacleSolid(s.builder, s.lead, s.frame, { center: mid, half: d.crown.half, y0: apexY - d.crown.below, shaftTop: apexY + d.crown.shaft, tipY: apexY + d.crown.tip });
}

function buildCrown(s: OudeKerkBuild): void {
  const { center, half, bodyY, lanternFoot, lanternTop, tipY, lanternRadius } = s.plan.tower;
  const p = TOWER.pinnacle;
  const reach = half - p.inset;
  for (const [su, sv] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
    const spec = { center: [center[0] + su * reach, center[1] + sv * reach] as const, half: p.half, y0: bodyY, shaftTop: bodyY + p.shaft, tipY: bodyY + p.tip };
    pinnacleSolid(s.builder, s.stone.wall, s.frame, spec);
  }
  const ring = squareRing(center, half);
  ring.forEach((a, i) => { buildClockDormer(s, a, ring[(i + 1) % 4]!); });
  const radius = (half - TOWER.needleInset) / Math.cos(Math.PI / 8);
  const styles = { slate: s.stone.roof, lead: s.lead, opening: s.stone.opening, stone: s.stone.wall };
  needleSpire(s.builder, styles, s.frame, { center, radius, y0: bodyY, lanternFoot, lanternTop, tipY, lanternRadius });
}

/** The octagonal stair turret against the south aisle's west front, lit by slits. */
function buildStairTurret(s: OudeKerkBuild): void {
  const { center, radius, bodyY, tipY } = s.plan.stairTurret;
  const c: Vec2 = [center[0], center[1]];
  const ring = ringPoints(c, radius, 8, Math.PI / 8);
  const face = Math.hypot(ring[1]![0] - ring[0]![0], ring[1]![1] - ring[0]![1]);
  const t = TOWER.turretSlit;
  const count = Math.floor((bodyY - t.height) / t.every);
  const slits = Array.from({ length: count }, (_, k): Ring => {
    const y = (k + 1) * t.every;
    return [[face / 2 - t.width / 2, y], [face / 2 + t.width / 2, y], [face / 2 + t.width / 2, y + t.height], [face / 2 - t.width / 2, y + t.height]];
  });
  ring.forEach((from, i) => { rectWall(s.builder, s.stone, s.frame, [from, ring[(i + 1) % 8]!], [0, bodyY], slits); });
  pyramidOverRing(s.builder, { roof: s.stone.roof, wall: s.stone.wall }, s.frame, ring, bodyY, { plan: c, y: tipY });
  octahedronSolid(s.builder, s.stone.wall, s.frame, planPoint(c[0], tipY + 0.1, c[1]), 0.25);
}

/* The tower's openings keep their reveals; their tracery is not recorded. */
export function buildOudeKerkTower(s: OudeKerkBuild): void {
  const tower = { ...s, stone: withoutTracery(s.stone) };
  buildShaft(tower);
  buildCrown(tower);
  buildStairTurret(tower);
}
